import { Component, Input, OnChanges, SimpleChanges, ViewChild, ElementRef, inject } from '@angular/core';
import { MaterialModule} from '@app/material.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { User } from '@interfaces/user';
import { UserApiService } from '@services/api/user/user/user-api.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import { Subject, takeUntil, Observable } from 'rxjs';
import {UserStorageService} from '@services/local-storage/user-storage/user-storage.service';
import {
  fileSizeValidator,
  fileTypeValidator
} from '@core/shared/form-validators';
import { CommonModule } from '@angular/common';
import {ToastrServices} from '@services/toastr/toastr.service';

@Component({
  selector: 'app-user-profile-image',
  imports: [MaterialModule, TranslateModule, ReactiveFormsModule, CommonModule],
  templateUrl: './user-profile-image.component.html',
  standalone: true,
  styleUrl: './user-profile-image.component.scss'
})
export class UserProfileImageComponent implements OnChanges {
  @Input() user: User | null = null;
  @ViewChild('fileInput') fileInput!: ElementRef;
  avatar:string = '/assets/images/users/user-img.jpg';
  // Without a photo, "Annulla" goes back to the default image instead of an empty (broken) one
  initialAvatar: string = '/assets/images/users/user-img.jpg';
  selectedFile: File | null = null;
  profileForm: FormGroup;
  private destroy$ = new Subject<void>();
  private user_api_service = inject(UserApiService);
  private user_storage_service = inject(UserStorageService);
  private toastr_service = inject(ToastrServices);
  private translate = inject(TranslateService);

  constructor(private fb: FormBuilder) {
    // Solo JPG e PNG, come dice la nota sotto la foto (il selettore file esclude gia' le GIF)
    const allowedTypes = ['image/jpeg', 'image/png'];
    const maxSizeKB = 800;
    const maxSizeBytes = maxSizeKB * 1024;

    this.profileForm = this.fb.group({
      avatar: ['', [fileTypeValidator(allowedTypes), fileSizeValidator(maxSizeBytes), Validators.required]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user'] && changes['user'].currentValue) {
      this.avatar = changes['user'].currentValue.avatar || this.avatar;
      this.initialAvatar = changes['user'].currentValue.avatar || this.initialAvatar;
    }
  }

  onFileSelected(event: any): void {
    this.selectedFile = event.target.files[0];
    this.profileForm.patchValue({ avatar: this.selectedFile });
    this.profileForm.get('avatar')?.updateValueAndValidity();
    if (this.selectedFile) {

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.avatar = e.target.result;
      };
      reader.readAsDataURL(this.selectedFile);
    }
  }

  resetProfileImage(): void {
    this.avatar = this.initialAvatar;
    this.selectedFile = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private convertToBase64(file: File): Observable<string> {
    const reader = new FileReader();

    return new Observable<string>((observer) => {
      reader.onload = () => {
        observer.next(reader.result as string);
        observer.complete();
      };

      reader.onerror = (error) => {
        observer.error(error);
      };

      reader.readAsDataURL(file);
    });
  }

  onSubmit(): void {
    if (this.profileForm.valid && this.selectedFile) {
      this.convertToBase64(this.selectedFile).subscribe({
        next: (base64String) => {
          const payload = { avatar: base64String };
          this.user_api_service.updateImageUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
            next: (response) => {
              this.user_storage_service.setUserStorage(response.data);
              this.initialAvatar = this.avatar;
              this.selectedFile = null;
              if (this.fileInput) {
                this.fileInput.nativeElement.value = '';
              }
              this.toastr_service.showSuccess(this.translate.instant('alert.success_image_upload'), this.translate.instant('alert.success'));
            },
            error: (error) => {
              this.resetProfileImage();
              console.error('Error:', error);
              this.toastr_service.showError(this.translate.instant('alert.error_image_upload'), this.translate.instant('alert.error'));
            }
          });
        },
        error: (error) => {
          this.resetProfileImage();
          console.error('Error conversion to Base64:', error);
        }
      });
    } else {
      console.warn('Nessun file IMMAGINE selezionato.');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
